import type { CalendarCallback } from "@/lib/types";

/**
 * The rep's callbacks between calls (ACL-671, ADR-226 in the platform repo): today's, still to be
 * made, the people of one accident together. Pure; the Phone page's idle stage draws it
 * (`softphone/coming-up.tsx`).
 */

export interface ComingUpGroup {
  /** The accident's number, or null for a callback whose person is in no accident the reader sees. */
  accident: string | null;
  /** The people in the group, as "Dana and Paul Whitfield" when the surnames agree, else by full name. */
  who: string;
  rows: CalendarCallback[];
}

/** Still to be made: upcoming or due. */
export function stillToMake(callbacks: readonly CalendarCallback[]): CalendarCallback[] {
  return callbacks
    .filter((c) => c.status === "upcoming" || c.status === "due")
    .sort((a, b) => a.due_at.localeCompare(b.due_at));
}

/** The groups in the order of their first callback: one accident's people together, everyone else
 *  on their own. */
export function comingUpGroups(callbacks: readonly CalendarCallback[]): ComingUpGroup[] {
  const groups = new Map<string, ComingUpGroup>();
  for (const row of stillToMake(callbacks)) {
    const key = row.accident_number ? `accident:${row.accident_number}` : `one:${row.id}`;
    const found = groups.get(key);
    if (found) found.rows.push(row);
    else groups.set(key, { accident: row.accident_number ?? null, who: "", rows: [row] });
  }
  for (const group of groups.values()) group.who = whoOf(group.rows);
  return [...groups.values()];
}

/** "Dana and Paul Whitfield" for a family, "Dana Whitfield and Luis Ortega" otherwise; one name alone. */
export function whoOf(rows: readonly Pick<CalendarCallback, "contact_name">[]): string {
  const names = [...new Set(rows.map((r) => r.contact_name).filter((n): n is string => Boolean(n)))];
  if (names.length === 0) return "No name";
  if (names.length === 1) return names[0]!;
  const surnames = new Set(names.map((n) => n.trim().split(/\s+/).slice(1).join(" ")));
  const [surname] = surnames;
  if (surnames.size === 1 && surname) {
    const firsts = names.map((n) => n.trim().split(/\s+/)[0]);
    return `${firsts.slice(0, -1).join(", ")} and ${firsts[firsts.length - 1]} ${surname}`;
  }
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The time on the rep's own clock: "4:00 PM" today, "Tomorrow 10:00 AM", else "Thu 10:00 AM". */
export function comingUpTime(iso: string, now: Date = new Date(), zone?: string): string {
  const at = new Date(iso);
  const day = (d: Date) => d.toLocaleDateString("en-US", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" });
  const time = at.toLocaleTimeString("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" });
  if (day(at) === day(now)) return time;
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  if (day(at) === day(tomorrow)) return `Tomorrow ${time}`;
  return `${at.toLocaleDateString("en-US", { timeZone: zone, weekday: "short" })} ${time}`;
}

/** What the badge says of how the call is made. */
export function comingUpMode(row: Pick<CalendarCallback, "status" | "dialer">): { label: string; tone: "warning" | "accent" | "neutral" } {
  if (row.status === "due") return { label: "Due now", tone: "warning" };
  if (row.dialer === "power" || row.dialer === "preview") return { label: "Dialer calls you", tone: "accent" };
  return { label: "You call", tone: "neutral" };
}

/** The row's second line: role, purpose, the first backup time. */
export function comingUpDetail(row: Pick<CalendarCallback, "role" | "purpose" | "backup_times">, when: (iso: string) => string): string | null {
  const parts = [
    row.role ? row.role.charAt(0).toUpperCase() + row.role.slice(1) : null,
    row.purpose,
    row.backup_times.length > 0 ? `or else ${when(row.backup_times[0]!)}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** Today on the rep's clock, as the callbacks route takes a range. */
export function todaySpan(now: Date = new Date()): { start: string; end: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return { start: start.toISOString(), end: end.toISOString() };
}
